import { jsPDF } from "jspdf";
import * as QRCode from "qrcode";
import type { SchoolInfo } from "@/types";
import { drawImageContain, drawImageCover, loadPdfImage, pdfDate, type PdfImage } from "./pdfImages";

export interface CardStudent {
  firstName: string;
  lastName: string;
  matricule: string;
  gender: "M" | "F";
  birthDate: string;
  birthPlace?: string;
  className: string;
  /** URL de la photo (serveur ou data URL). */
  photo?: string;
}

/*
 * Format carte bancaire (ISO 7810 ID-1), en mm. Sur une page A4 : 4 élèves, une ligne chacun,
 * recto à gauche et verso à droite (découper puis coller ou plastifier dos à dos).
 */
const W = 85.6;
const H = 54;
const GAP = 5;
const ROWS = 4;
const LEFT = (210 - 2 * W - GAP) / 2;
const TOP = (297 - ROWS * H - (ROWS - 1) * GAP) / 2;
const RADIUS = 3;

type Rgb = [number, number, number];
const NAVY: Rgb = [30, 58, 138];
const GOLD: Rgb = [214, 158, 20];
const LAVENDER: Rgb = [203, 211, 240];
const TEXT: Rgb = [25, 25, 30];
const MUTED: Rgb = [110, 110, 120];

/** Coupe un texte trop long pour la largeur donnée (avec « … »). */
function fit(doc: jsPDF, text: string, width: number): string {
  if (doc.getTextWidth(text) <= width) return text;
  let cut = text;
  while (cut.length > 1 && doc.getTextWidth(`${cut}…`) > width) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
}

/** Texte du QR code : lisible par n'importe quel téléphone, sans connexion. */
function qrText(student: CardStudent, school: SchoolInfo, schoolYear: string): string {
  return [
    `CARTE SCOLAIRE ${schoolYear}`,
    school.name,
    `Matricule : ${student.matricule}`,
    `Nom : ${student.lastName.toUpperCase()} ${student.firstName}`,
    `Classe : ${student.className}`,
  ].join("\n");
}

/** QR code en vectoriel (net à toute taille), carré de côté `size` avec sa marge blanche. */
function drawQr(doc: jsPDF, text: string, x: number, y: number, size: number): void {
  const { modules } = QRCode.create(text, { errorCorrectionLevel: "M" });
  const count = modules.size;
  const quiet = 1.5;
  const cell = size / (count + 2 * quiet);
  doc.setFillColor(255, 255, 255);
  doc.rect(x, y, size, size, "F");
  doc.setFillColor(0, 0, 0);
  for (let row = 0; row < count; row++) {
    // Les modules noirs voisins d'une ligne sont dessinés d'un seul rectangle.
    for (let col = 0; col < count; ) {
      if (!modules.get(row, col)) {
        col++;
        continue;
      }
      const start = col;
      while (col < count && modules.get(row, col)) col++;
      doc.rect(x + (quiet + start) * cell, y + (quiet + row) * cell, (col - start) * cell + 0.01, cell + 0.01, "F");
    }
  }
}

/** Contour de découpe en pointillés. */
function cutLine(doc: jsPDF, x: number, y: number): void {
  doc.setDrawColor(170, 170, 165);
  doc.setLineWidth(0.2);
  doc.setLineDashPattern([1.2, 1], 0);
  doc.roundedRect(x, y, W, H, RADIUS, RADIUS, "S");
  doc.setLineDashPattern([], 0);
}

/** Logo de l'école, ou ses initiales dans un cercle s'il n'y en a pas. */
function drawLogo(doc: jsPDF, logo: PdfImage | null, school: SchoolInfo, x: number, y: number, size: number): void {
  if (logo) {
    drawImageContain(doc, logo, x, y, size, size);
    return;
  }
  const initials = school.name
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
  doc.setFillColor(...NAVY);
  doc.circle(x + size / 2, y + size / 2, size / 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(size * 1.1);
  doc.text(initials || "É", x + size / 2, y + size / 2 + size * 0.14, { align: "center" });
}

function drawFront(
  doc: jsPDF,
  x: number,
  y: number,
  student: CardStudent,
  photo: PdfImage | null,
  logo: PdfImage | null,
  school: SchoolInfo,
  schoolYear: string,
): void {
  // Logo à gauche, QR code à droite, en-tête officiel au centre.
  drawLogo(doc, logo, school, x + 3.5, y + 3, 12);
  drawQr(doc, qrText(student, school, schoolYear), x + W - 16, y + 2.5, 13);

  const cx = x + W / 2;
  const headerWidth = W - 36;
  let line = y + 4.8;
  if (school.stateName.trim()) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.3);
    doc.setTextColor(...GOLD);
    doc.text(fit(doc, school.stateName.trim().toUpperCase(), headerWidth), cx, line, { align: "center" });
    line += 2.4;
  }
  if (school.stateMotto.trim()) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(4.6);
    doc.setTextColor(...TEXT);
    doc.text(fit(doc, school.stateMotto.trim(), headerWidth), cx, line, { align: "center" });
    line += 2.1;
  }
  if (school.ministryName.trim()) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(4.3);
    doc.setTextColor(...NAVY);
    const lines = (doc.splitTextToSize(school.ministryName.trim(), headerWidth) as string[]).slice(0, 2);
    lines.forEach((text, i) => doc.text(text, cx, line + i * 1.8, { align: "center" }));
  }

  // Bandeau « CARTE SCOLAIRE » et année.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.6);
  const title = "CARTE SCOLAIRE";
  const charSpace = 0.45;
  const titleWidth = doc.getTextWidth(title) + charSpace * (title.length - 1);
  doc.setFillColor(...NAVY);
  doc.roundedRect(cx - titleWidth / 2 - 4, y + 12.6, titleWidth + 8, 4.2, 2.1, 2.1, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(title, cx - titleWidth / 2, y + 15.65, { charSpace });
  doc.setFontSize(4.2);
  doc.setTextColor(...NAVY);
  const year = `ANNÉE SCOLAIRE : ${schoolYear}`;
  doc.text(year, cx, y + 19, { align: "center" });
  const yearWidth = doc.getTextWidth(year);
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.15);
  doc.line(cx - yearWidth / 2, y + 19.5, cx + yearWidth / 2, y + 19.5);

  // Photo d'identité, encadrée de blanc avec une légère ombre.
  const px = x + 4;
  const py = y + 20.8;
  const pw = 19;
  const ph = 23.5;
  doc.setFillColor(215, 215, 222);
  doc.rect(px + 0.5, py + 0.5, pw, ph, "F");
  if (photo) {
    drawImageCover(doc, photo, px, py, pw, ph);
  } else {
    doc.setFillColor(236, 238, 243);
    doc.rect(px, py, pw, ph, "F");
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`${student.firstName[0] ?? ""}${student.lastName[0] ?? ""}`.toUpperCase(), px + pw / 2, py + ph / 2 + 2, {
      align: "center",
    });
  }
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.6);
  doc.rect(px, py, pw, ph, "S");

  // Identité : libellé puis valeur en gras sur la même ligne.
  const fx = x + 26;
  const right = x + W - 3.5;
  const field = (label: string, value: string, top: number, maxRight = right) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.6);
    doc.setTextColor(...NAVY);
    doc.text(`${label} : `, fx, top);
    const vx = fx + doc.getTextWidth(`${label} : `);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...TEXT);
    doc.text(fit(doc, value || "—", maxRight - vx), vx, top);
  };
  field("Matricule", student.matricule, y + 24.6);
  field("Nom et Prénoms", `${student.lastName.toUpperCase()} ${student.firstName}`, y + 29.1);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  const sexWidth = doc.getTextWidth(student.gender);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.6);
  const sexLabelWidth = doc.getTextWidth("Sexe : ");
  field("Date de naissance", student.birthDate ? pdfDate(student.birthDate) : "", y + 33.6, right - sexWidth - sexLabelWidth - 3);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.6);
  doc.setTextColor(...NAVY);
  doc.text("Sexe : ", right - sexWidth - sexLabelWidth, y + 33.6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...TEXT);
  doc.text(student.gender, right, y + 33.6, { align: "right" });
  field("Lieu de naissance", student.birthPlace ?? "", y + 38.1);
  field("Classe", student.className, y + 42.6, right - 22);

  // Signature du chef d'établissement.
  const signatoryTitle = school.signatoryTitle.trim() || "Le Directeur";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(4.4);
  doc.setTextColor(...TEXT);
  if (school.signatoryName.trim()) doc.text(fit(doc, school.signatoryName.trim(), 21), right, y + 41, { align: "right" });
  doc.setFont("helvetica", "italic");
  doc.setFontSize(4);
  doc.setTextColor(...MUTED);
  doc.text(fit(doc, signatoryTitle, 21), right, y + 43, { align: "right" });

  // Bande du bas : partie bleu foncé en biais à gauche, nom de l'école sur fond clair.
  const by = y + 46.3;
  const bh = H - 46.3;
  doc.setFillColor(...LAVENDER);
  doc.roundedRect(x, by, W, bh, RADIUS, RADIUS, "F");
  doc.rect(x, by, W, bh - RADIUS, "F");
  doc.setFillColor(...NAVY);
  doc.roundedRect(x, by - 1.2, 22, bh + 1.2, RADIUS, RADIUS, "F");
  doc.rect(x, by - 1.2, 22, bh + 1.2 - RADIUS, "F");
  doc.rect(x + RADIUS, by - 1.2, 22 - RADIUS, bh + 1.2, "F");
  doc.triangle(x + 22, by - 1.2, x + 26, y + H, x + 22, y + H, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.2);
  doc.setTextColor(...NAVY);
  const name = fit(doc, school.name.toUpperCase(), W - 33);
  const nameSpace = 0.35;
  const nameWidth = doc.getTextWidth(name) + nameSpace * (name.length - 1);
  doc.text(name, x + 27 + (W - 30 - nameWidth) / 2, by + bh / 2 + 1.3, { charSpace: nameSpace });
}

function drawBack(doc: jsPDF, x: number, y: number, logo: PdfImage | null, school: SchoolInfo): void {
  const cx = x + W / 2;
  // Étoile dorée au-dessus du logo, comme sur le modèle.
  doc.setFillColor(...GOLD);
  const star = (r: number, i: number) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    return [cx + r * Math.cos(a), y + 5.6 + r * Math.sin(a)] as const;
  };
  const points = Array.from({ length: 10 }, (_, i) => star(i % 2 === 0 ? 1.6 : 0.65, i));
  doc.lines(
    points.slice(1).map(([px, py], i) => [px - points[i][0], py - points[i][1]]),
    points[0][0],
    points[0][1],
    [1, 1],
    "F",
    true,
  );

  drawLogo(doc, logo, school, cx - 9.5, y + 8, 19);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.6);
  doc.setTextColor(...NAVY);
  doc.text(fit(doc, school.name.toUpperCase(), W - 10), cx, y + 31, { align: "center", charSpace: 0.2 });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.2);
  doc.setTextColor(...TEXT);
  const notice =
    "Cette carte est strictement personnelle et obligatoire pour toutes démarches scolaires et administratives. " +
    "Toute reproduction, altération ou usage frauduleux est formellement interdit et expose son auteur à des " +
    "poursuites judiciaires.";
  const lines = doc.splitTextToSize(notice, W - 12) as string[];
  lines.forEach((text, i) => doc.text(text, cx, y + 36.5 + i * 2.3, { align: "center" }));

  const contact = school.phone.trim() || school.email.trim();
  const lost = contact
    ? `En cas de perte, prière de contacter l'établissement au : ${contact}`
    : "En cas de perte, prière de la rapporter à l'établissement.";
  doc.text(fit(doc, lost, W - 8), cx, y + 36.5 + lines.length * 2.3 + 2.6, { align: "center" });
}

/**
 * Cartes scolaires recto-verso, 4 élèves par page A4 (recto à gauche, verso à droite, à découper le long
 * des pointillés). Recto : en-tête officiel, logo, QR code, photo, identité, signature et nom de l'école.
 * Verso : logo, règles d'usage et contact en cas de perte.
 */
export async function exportStudentCardsPdf(
  students: CardStudent[],
  school: SchoolInfo,
  schoolYear: string,
  fileName: string,
): Promise<void> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const [logo, photos] = await Promise.all([
    loadPdfImage(school.logo),
    Promise.all(students.map((s) => loadPdfImage(s.photo))),
  ]);

  students.forEach((student, index) => {
    const slot = index % ROWS;
    if (index > 0 && slot === 0) doc.addPage();
    if (slot === 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text("RECTO", LEFT + W / 2, TOP - 3, { align: "center" });
      doc.text("VERSO", LEFT + W + GAP + W / 2, TOP - 3, { align: "center" });
    }
    const y = TOP + slot * (H + GAP);
    const back = LEFT + W + GAP;
    drawFront(doc, LEFT, y, student, photos[index], logo, school, schoolYear);
    drawBack(doc, back, y, logo, school);
    cutLine(doc, LEFT, y);
    cutLine(doc, back, y);
  });

  doc.save(fileName);
}
